import React, { useState, useEffect, useCallback } from 'react';
import {
  Brain,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  ChevronRight,
  Loader2,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function PHI3ReviewQueue() {
  const [status, setStatus] = useState(null);
  const [queueStats, setQueueStats] = useState(null);
  const [pendingTenders, setPendingTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingSingle, setProcessingSingle] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, statsRes, pendingRes] = await Promise.all([
        fetch(`${API_URL}/api/phi3/status`),
        fetch(`${API_URL}/api/phi3/review-queue/stats`),
        fetch(`${API_URL}/api/phi3/review-queue/pending?limit=10`)
      ]);

      const statusData = await statusRes.json();
      const statsData = await statsRes.json();
      const pendingData = await pendingRes.json();

      setStatus(statusData);
      setQueueStats(statsData);
      setPendingTenders(pendingData.tenders || []);
    } catch (error) {
      console.error('Error fetching PHI-3 data:', error);
      toast.error('Failed to fetch PHI-3 status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleProcessQueue = async (limit = 5) => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/phi3/review-queue/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit, update_db: true })
      });

      const data = await response.json();

      if (data.status === 'processing') {
        toast.info(`Processing ${limit} tenders in background (~${Math.round(limit * 0.8)} min)`);
      } else if (data.processed !== undefined) {
        toast.success(`Processed ${data.processed} tenders. ${data.classification_changed} changed.`);
        fetchData(); // Refresh data
      } else if (data.error) {
        toast.error(data.error);
      }
    } catch (error) {
      console.error('Error processing queue:', error);
      toast.error('Failed to process review queue');
    } finally {
      setProcessing(false);
    }
  };

  const handleReviewSingle = async (tenderId) => {
    setProcessingSingle(tenderId);
    try {
      const response = await fetch(`${API_URL}/api/phi3/review-single/${tenderId}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        if (data.changed) {
          toast.success(`Classification changed: ${data.old_company} → ${data.new_company}`);
        } else {
          toast.info(`Classification confirmed: ${data.new_company} (${Math.round(data.confidence * 100)}%)`);
        }
        fetchData(); // Refresh data
      } else {
        toast.warning(data.error || 'PHI-3 returned low confidence');
      }
    } catch (error) {
      console.error('Error reviewing tender:', error);
      toast.error('Failed to review tender');
    } finally {
      setProcessingSingle(null);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === 'HIGH') return 'bg-green-100 text-green-800';
    if (confidence === 'MEDIUM') return 'bg-yellow-100 text-yellow-800';
    if (confidence === 'LOW') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="phi3-review-queue">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            <span>PHI-3 AI Review Queue</span>
          </div>
          <div className="flex items-center gap-2">
            {status?.healthy ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Online
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <AlertCircle className="h-3 w-3 mr-1" />
                Offline
              </Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Queue Stats */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {queueStats?.needs_review || 0}
            </div>
            <div className="text-xs text-muted-foreground">Pending Review</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {queueStats?.already_reviewed || 0}
            </div>
            <div className="text-xs text-muted-foreground">Already Reviewed</div>
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {queueStats?.low_confidence_pending || 0}
            </div>
            <div className="text-xs text-muted-foreground">Low Confidence</div>
          </div>
        </div>

        {/* Process Buttons */}
        {queueStats?.needs_review > 0 && status?.healthy && (
          <div className="flex gap-2 mb-4">
            <Button
              onClick={() => handleProcessQueue(5)}
              disabled={processing}
              size="sm"
              className="flex-1"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Process 5 (~4 min)
            </Button>
            <Button
              onClick={() => handleProcessQueue(10)}
              disabled={processing}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Clock className="h-4 w-4 mr-2" />
              )}
              Process 10 (~8 min)
            </Button>
          </div>
        )}

        {/* Pending Tenders List */}
        {pendingTenders.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <div className="text-sm font-medium text-muted-foreground mb-2">
              Tenders Needing Review
            </div>
            {pendingTenders.map((tender) => (
              <div
                key={tender._id}
                className="flex items-center justify-between p-2 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                data-testid={`pending-tender-${tender._id}`}
              >
                <div className="flex-1 min-w-0 mr-2">
                  <div className="text-sm font-medium truncate">
                    {tender.title?.substring(0, 60)}...
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge
                      variant="outline"
                      className={getConfidenceColor(tender.classification_confidence)}
                    >
                      {tender.classification_confidence || 'Unknown'}
                    </Badge>
                    <span>{tender.company || 'Unclassified'}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleReviewSingle(tender._id)}
                  disabled={processingSingle === tender._id || !status?.healthy}
                >
                  {processingSingle === tender._id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
            <div className="text-sm">All tenders reviewed!</div>
            <div className="text-xs">Run a scrape to find new tenders</div>
          </div>
        )}

        {/* Info Footer */}
        <div className="mt-4 pt-3 border-t text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Brain className="h-3 w-3" />
            PHI-3 AI classifies uncertain tenders with ~45s processing time each
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default PHI3ReviewQueue;
